import {
  HHButton,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import AddIcon from '@mui/icons-material/Add';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { NoRowsOverlay } from '../../../billing-tool/components/grid-components/no-rows-or-error-overlay';
import { useDynamicDrawerContext } from '../../../billing-tool/context/dynamic-drawer.context';
import { useSnackbarContext } from '../../../billing-tool/context/snackbar.context';
import { formatDate } from '../../../billing-tool/utils/date-helper';
import {
  useCreateSubscriptionEstimateMutation,
  useSubscriptionEstimatesQuery,
  useUpdateSubscriptionEstimateMutation,
} from '../../types';
import { SubscriptionEstimateForm } from '../subscription-estimate-form/form';
import { EstimatePeriod } from '../subscription-estimate-form/period-validation';
import { SubscriptionEstimateFormValues } from '../subscription-estimate-form/validation-schema';
import { StatusQuickFilters } from './status-quick-filters';
import {
  SubscriptionEstimateColumnFields,
  SubscriptionEstimateRow,
  SubscriptionEstimateStatusFilter,
  SUBSCRIPTION_ESTIMATES_ADD_BUTTON_TEST_ID,
} from './types';
import { useSubscriptionEstimateColumns } from './use-subscription-estimate-columns';
import {
  toFormReserve,
  toStoredReserve,
  toSubscriptionEstimateRow,
} from './utils';

const toDateString = (date: Date | null): string | null =>
  date ? formatDate(date, 'YYYY-MM-DD') : null;

export const SUBSCRIPTION_ESTIMATES_GLOBAL_GRID_TEST_ID =
  'subscription-estimates-global-grid';

interface SectionGridProps {
  testId: string;
  ariaLabel: string;
  heading?: string;
  subtext?: string;
  columns: GridColDef[];
  rows: SubscriptionEstimateRow[];
  loading: boolean;
  error: boolean;
}

const SectionGrid = ({
  testId,
  ariaLabel,
  heading,
  subtext,
  columns,
  rows,
  loading,
  error,
}: SectionGridProps): JSX.Element => (
  <HHStack direction="column" spacing={1}>
    {(heading || subtext) && (
      <HHStack direction="row" spacing={2} alignItems="baseline">
        <HHTypography hhVariant="subtitle1" sx={{ fontWeight: 700 }}>
          {heading}
        </HHTypography>
        <HHTypography
          hhVariant="body2"
          color={(theme): string => theme.palette.text.secondary}
        >
          {subtext}
        </HHTypography>
      </HHStack>
    )}
    <DataGridPro
      data-testid={testId}
      aria-label={ariaLabel}
      autoHeight
      columns={columns}
      rows={rows}
      getRowId={(row): number => row.id}
      loading={loading}
      pinnedColumns={{ right: [SubscriptionEstimateColumnFields.ACTIONS] }}
      {...(process.env.NODE_ENV === 'test' && {
        disableVirtualization: true,
      })}
      hideFooter
      slots={{
        noResultsOverlay: NoRowsOverlay,
        noRowsOverlay: NoRowsOverlay,
      }}
      slotProps={{
        noResultsOverlay: { error } as Record<string, boolean>,
        noRowsOverlay: { error } as Record<string, boolean>,
      }}
    />
  </HHStack>
);

const SubscriptionEstimatesGrid = (): JSX.Element => {
  const { setDrawerContent, onClose } = useDynamicDrawerContext();
  const { setSnackbarState } = useSnackbarContext();

  const [statusFilter, setStatusFilter] =
    useState<SubscriptionEstimateStatusFilter>('all');

  const [createSubscriptionEstimate] = useCreateSubscriptionEstimateMutation();
  const [updateSubscriptionEstimate] = useUpdateSubscriptionEstimateMutation();

  const {
    data: estimatesData,
    loading: estimatesLoading,
    error: estimatesError,
  } = useSubscriptionEstimatesQuery();

  // Global-scope estimates the drawer form validates the candidate period
  // against (overlap block, gap warn). Mirrors the grid's global-row filter.
  const existingEstimates = useMemo<EstimatePeriod[]>(
    () =>
      (estimatesData?.subscriptionEstimates ?? [])
        .filter(estimate => estimate.contractId === null)
        .map(estimate => ({
          id: estimate.id,
          startDate: estimate.startDate,
          endDate: estimate.endDate ?? null,
        })),
    [estimatesData],
  );

  const handleAdd = useCallback((): void => {
    const handleCreate = async (
      values: SubscriptionEstimateFormValues,
    ): Promise<void> => {
      try {
        await createSubscriptionEstimate({
          variables: {
            // Global scope: contractId omitted (null).
            input: {
              startDate: toDateString(values.startDate) as string,
              endDate: toDateString(values.endDate),
              grossAmount: values.grossAmount as number,
              reserveAmount: toStoredReserve(values.reserveAmount as number),
            },
          },
          refetchQueries: ['subscriptionEstimates'],
        });
        setSnackbarState(
          'Subscription estimate created successfully',
          'success',
          3000,
        );
        onClose();
      } catch (e) {
        const message =
          e instanceof Error
            ? e.message
            : 'Error creating subscription estimate';
        setSnackbarState(message, 'error', 5000);
      }
    };

    setDrawerContent(
      <HHStack padding={4}>
        <SubscriptionEstimateForm
          onSubmit={handleCreate}
          existingEstimates={existingEstimates}
        />
      </HHStack>,
      <HHTypography hhVariant="h6">Add Subscription Estimate</HHTypography>,
      undefined,
      'temporary',
    );
  }, [
    setDrawerContent,
    createSubscriptionEstimate,
    setSnackbarState,
    onClose,
    existingEstimates,
  ]);

  const handleEdit = useCallback(
    (row: SubscriptionEstimateRow): void => {
      const initialValues: SubscriptionEstimateFormValues = {
        grossAmount: row.grossAmount,
        reserveAmount: toFormReserve(row.reserveAmount),
        startDate: dayjs(row.startDate).toDate(),
        endDate: row.endDate ? dayjs(row.endDate).toDate() : null,
      };

      const handleUpdate = async (
        values: SubscriptionEstimateFormValues,
      ): Promise<void> => {
        try {
          await updateSubscriptionEstimate({
            variables: {
              id: row.id,
              // UpdateSubscriptionEstimateInput: endDate/grossAmount/reserveAmount only.
              input: {
                endDate: toDateString(values.endDate),
                grossAmount: values.grossAmount as number,
                reserveAmount: toStoredReserve(values.reserveAmount as number),
              },
            },
            refetchQueries: ['subscriptionEstimates'],
          });
          setSnackbarState(
            'Subscription estimate updated successfully',
            'success',
            3000,
          );
          onClose();
        } catch (e) {
          const message =
            e instanceof Error
              ? e.message
              : 'Error updating subscription estimate';
          setSnackbarState(message, 'error', 5000);
        }
      };

      setDrawerContent(
        <HHStack padding={4}>
          <SubscriptionEstimateForm
            onSubmit={handleUpdate}
            initialValues={initialValues}
            id={row.id}
            status={row.status}
            existingEstimates={existingEstimates}
          />
        </HHStack>,
        <HHTypography hhVariant="h6">Edit Subscription Estimate</HHTypography>,
        undefined,
        'temporary',
      );
    },
    [
      setDrawerContent,
      updateSubscriptionEstimate,
      setSnackbarState,
      onClose,
      existingEstimates,
    ],
  );

  const columns = useSubscriptionEstimateColumns({ onEdit: handleEdit });

  const rows = (estimatesData?.subscriptionEstimates ?? [])
    .filter(estimate => estimate.contractId === null)
    .map(toSubscriptionEstimateRow);

  // Client-side status quick-filter over the already-fetched rows. 'all' clears
  // the filter; each ActiveStatus narrows to rows with that derived status.
  const visibleRows =
    statusFilter === 'all'
      ? rows
      : rows.filter(row => row.status === statusFilter);

  return (
    <HHStack direction="column" spacing={4}>
      <HHStack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ paddingLeft: 6, paddingRight: 6, paddingTop: 6 }}
      >
        <HHTypography hhVariant="section-title">
          Subscription Revenue Recognition
        </HHTypography>
        <HHButton
          hhVariant="variant-bypass"
          variant="contained"
          size="medium"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          data-testid={SUBSCRIPTION_ESTIMATES_ADD_BUTTON_TEST_ID}
        >
          Add Estimate
        </HHButton>
      </HHStack>

      <StatusQuickFilters selected={statusFilter} onSelect={setStatusFilter} />

      <SectionGrid
        testId={SUBSCRIPTION_ESTIMATES_GLOBAL_GRID_TEST_ID}
        ariaLabel="Global default subscription estimates table"
        columns={columns}
        rows={visibleRows}
        loading={estimatesLoading}
        error={!!estimatesError}
      />
    </HHStack>
  );
};

export default SubscriptionEstimatesGrid;
